import { useLocation, useParams } from "react-router-dom"
import { DataReceiptsOrder } from "../api/DataReceiptApi"
import { NexPageSize, nexDefaultPagination, nexPageSizes } from "../components/Table/pageable-table/NexPageSize"
import { PagingFilters } from "../components/Table/pageable-table/PagingFilters"
import { Order } from "../models/SortOrder"
import { DataReceiptSearchFilters } from "../pages/DataReceipt/table-view/DataReceiptSearchFilters"
import {
    SensorInstallFiltersAndPaging,
    SensorInstallOrder,
    SensorInstallSearchFilters,
    sensorInstallDefaultOrder,
    sensorInstallDefaultOrderBy,
} from "../pages/SensorInstallation/table/SensorInstallSearchFilters"
import { OfficeId } from "../store/types/MissionsTypes"
import { NumberUtils } from "../utils/NumberUtils"

const useNexusPagingParams = <T extends string>() => {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const order = queryParams.get("order") ?? undefined
    const orderBy = queryParams.get("ordering") ?? undefined
    let pageSize = NumberUtils.toNumberOrUndefined(queryParams.get("page_size"))

    if (pageSize != null && !(nexPageSizes as number[]).includes(pageSize)) {
        pageSize = undefined
    }

    return {
        page: NumberUtils.toNumberOrUndefined(queryParams.get("page")),
        page_size: pageSize as NexPageSize,
        order: order as Order | undefined,
        ordering: orderBy as T | undefined,
    }
}

export const useNexusProjectNumber = () => {
    const params = useParams()
    if (params.projectNumber == null) {
        throw new Error("Error getting projectNumber from path parameters. Required.")
    }
    return params.projectNumber
}

export const useNexusProjectNumberOptional = () => {
    const params = useParams()
    return params.projectNumber
}

export const useNexusFlightplanFlow = () => {
    const params = useParams()
    if (params.flightplanFlow == null) {
        throw new Error("Error getting flightplanFlow from path parameters. Required.")
    }
    return params.flightplanFlow
}

export const useNexusFlightPlanIdOptional = () => {
    const params = useParams()
    return params.flightPlanId
}

export const useNexusMissionFlow = () => {
    const params = useParams()
    if (params.missionFlow == null) {
        throw new Error("Error getting missionFlow from path parameters. Required.")
    }
    return params.missionFlow
}

export const useNexusMissionId = () => {
    const params = useParams()
    if (params.missionId == null) {
        throw new Error("Error getting missionId from path parameters. Required.")
    }
    return params.missionId
}

export const useNexusMissionIdOptional = () => {
    const params = useParams()
    return params.missionId
}

export const useNexusDataReceiptIdOptional = () => {
    const params = useParams()
    return params.dataReceiptId
}

export const useNexusSensorIdOptional = () => {
    const params = useParams()
    return params.sensorId
}

export const useNexusFlightplanIds = () => {
    const location = useLocation()
    const queryString = location.search
    const queryParams = new URLSearchParams(queryString)
    return queryParams.getAll("flight-plan-id").map((flightPlanId) => Number(flightPlanId))
}

export const useNexusAoiId = () => {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const aoiId = queryParams.get("aoiId")

    return aoiId == null ? undefined : Number(aoiId)
}

export const useDataReceiptsSearchFilters = (): DataReceiptSearchFilters &
    Partial<PagingFilters<DataReceiptsOrder>> => {
    const pagingParams = useNexusPagingParams<DataReceiptsOrder>()
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)

    const office = queryParams.get("office") ?? undefined
    const mission_name = queryParams.get("mission_name") ?? undefined
    const project_name_or_number = queryParams.get("project_name_or_number") ?? undefined
    const source = queryParams.get("source") ?? undefined
    const tracking_number = queryParams.get("tracking_number") ?? undefined
    const received_start_date = queryParams.get("received_start_date") ?? undefined
    const received_end_date = queryParams.get("received_end_date") ?? undefined

    return {
        ...pagingParams,
        office: office as OfficeId | undefined,
        mission_name,
        project_name_or_number,
        source,
        tracking_number,
        received_start_date,
        received_end_date,
    }
}

export const useSensorInstallSearchFilters = (): SensorInstallSearchFilters &
    Partial<PagingFilters<SensorInstallOrder>> => {
    const pagingParams = useNexusPagingParams<SensorInstallOrder>()
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)

    const file_name = queryParams.get("file_name") ?? undefined
    const valid_on_date = queryParams.get("valid_on_date") ?? undefined
    const sensor_search = queryParams.get("sensor_search") ?? undefined

    return {
        ...pagingParams,
        file_name,
        valid_on_date,
        sensor_search,
    }
}

export const useSensorInstallSearchFiltersWithDefaults = (): SensorInstallFiltersAndPaging => {
    const { page, page_size, order, ordering, ...rest } = useSensorInstallSearchFilters()
    const pageWithDefault = page ?? nexDefaultPagination.page
    const pageSizeWithDefault = page_size ?? nexDefaultPagination.pageSize
    const orderWithDefault = order ?? sensorInstallDefaultOrder
    const orderByWithDefault = ordering ?? sensorInstallDefaultOrderBy
    return {
        page: pageWithDefault,
        page_size: pageSizeWithDefault,
        order: orderWithDefault,
        ordering: orderByWithDefault,
        ...rest,
    }
}
